import { NextSeo } from 'next-seo';
import { SEO } from 'next-seo.config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';

import { useCookieAuth } from '@hooks/useCookieAuth';
// components;
import MainHeader from '@layouts/MainHeader';
import { locationActions, orderActions, userActions } from '@store/actions';
import { orderSelector } from '@store/order/order.slice';
import { userSelector } from '@store/user/user.slice';

import productApi from '../apis/product';

const MainFooter = dynamic(() => import('@layouts/MainFooter'));

interface IProps {
  children: ReactNode;
  title?: string;
  description?: string;
  openGraphImage?: string;
  path?: string;
}

export default function PageLayout({
  children,
  title = '',
  description = '',
  openGraphImage = '',
  path,
}: IProps) {
  const ref = useRef(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const { getToken } = useCookieAuth();
  const { isAuth } = useSelector(userSelector);
  const { cartItems } = useSelector(orderSelector);

  const accessToken = getToken('access_token');

  const getCartPrice = async (cart: { productId: string; quantity: number }[]) => {
    try {
      const { data } = await productApi.getListCartProduct(cart);
      dispatch(orderActions.self.setCartItems(data?.data || []));
    } catch (error) {
      dispatch(orderActions.self.setCartItems([]));
    }
  };

  useEffect(() => {
    dispatch(locationActions.getLocations());
  }, []);

  useEffect(() => {
    if (accessToken) {
      dispatch(userActions.getUserInfo());
    }
  }, [accessToken]);

  useEffect(() => {
    if (cartItems?.length) {
      const cart = cartItems?.map((item) => ({
        productId: item._id,
        quantity: item.quantity,
      }));
      getCartPrice(cart);
    }
  }, [isAuth]);

  useEffect(() => {
    const handleStart = (url: string) => url !== router.asPath && ref.current.continuousStart();
    const handleComplete = () => {
      ref.current?.complete();
    };
    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);
    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  });

  const canonical = `https://thucphamquangda.com${path || router.asPath}`;

  return (
    <div className="relative">
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          url: canonical,
          title: title,
          description: description,
          images: [
            {
              url: openGraphImage || SEO?.openGraph?.images?.[0]?.url,
              alt: title,
              width: 800,
              height: 600,
            },
          ],
          siteName: '@thucphamquangda.com',
        }}
      />
      <LoadingBar color="#f97316" ref={ref} />
      <MainHeader showAuthButtons={isAuth} />

      <div className="min-h-[70vh]">{children}</div>
      <MainFooter />
    </div>
  );
}
